import { Box, Button, FormControlLabel, Input, Switch, Typography } from '@mui/material';
import Slider from '@mui/material/Slider';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { setHistorySelectedTime, setHistoryTracksVisible, setIsHistoryOpen, setTransportConditionSelectedItem, setTransportSelectedDate, } from 'app/actions/map';
import { ArrowLeft, ArrowRight } from 'app/icons';
import { useAsyncAction, useDebounce } from 'app/services';
import { transportHistoryTracksItemsLoadStream, transportHistoryTracksItemsLoadStreamPriority } from 'app/store/map';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
const HistoryTimeLime = () => {
    const dispatch = useDispatch();
    const { transportSelectedDate, historySelectedTime, isHistoryOpen, transportCheckedItems, historyTracksVisible, transportSelectedItem, historyTracksProgress, historyTracksLoading, } = useSelector((state) => state.map);
    const { asyncAction: fetchTransportHistoryTracksItems } = useAsyncAction(transportHistoryTracksItemsLoadStream);
    const [selectedTime, setSelectedTime] = useState(0);
    const prevDateRef = useRef(null);
    const priorityLoadTimeoutRef = useRef(null);
    const isPriorityLoadingRef = useRef(false);
    const transportCheckedItemsKey = useMemo(() => transportCheckedItems.join(','), [transportCheckedItems]);
    // Вычисляем selectedTime из historySelectedTime
    useEffect(() => {
        if (!historySelectedTime || !transportSelectedDate[0]) {
            setSelectedTime(0);
            return;
        }
        const historySelected = moment(historySelectedTime);
        const startDate = moment(transportSelectedDate[0]);
        if (!historySelected.isValid() || !startDate.isValid()) {
            setSelectedTime(0);
            return;
        }
        const diff = Math.round(historySelected.diff(startDate, 's'));
        setSelectedTime(isNaN(diff) ? 0 : diff);
    }, [historySelectedTime, transportSelectedDate[0]]);
    useEffect(() => {
        if (!historyTracksLoading && isHistoryOpen && transportSelectedDate[0] && transportSelectedDate[1]) {
            const currentDateKey = `${transportSelectedDate[0]}_${transportSelectedDate[1]}_${transportCheckedItemsKey}`;
            // Проверяем, изменились ли даты или список транспорта
            if (prevDateRef.current !== currentDateKey) {
                prevDateRef.current = currentDateKey;
                fetchTransportHistoryTracksItems();
            }
        }
    }, [isHistoryOpen, transportSelectedDate[0], transportSelectedDate[1], transportCheckedItemsKey]);
    useEffect(() => {
        if (!(transportCheckedItems === null || transportCheckedItems === void 0 ? void 0 : transportCheckedItems.length)) {
            dispatch(setIsHistoryOpen(false));
        }
    }, [transportCheckedItems]);
    // Очистка timeout при размонтировании
    useEffect(() => {
        return () => {
            if (priorityLoadTimeoutRef.current) {
                clearTimeout(priorityLoadTimeoutRef.current);
            }
        };
    }, []);
    const startDate = useMemo(() => {
        if (!transportSelectedDate[0])
            return moment();
        return moment(moment(transportSelectedDate[0]).format());
    }, [transportSelectedDate]);
    const valueLabelFormat = useMemo(() => {
        if (!startDate.isValid())
            return '';
        return moment(startDate)
            .add(selectedTime || 0, 's')
            .format('YYYY-MM-DD HH:mm:ss');
    }, [startDate, selectedTime]);
    const handleSetHistoryTime = useDebounce((newHistorySelectedTime) => {
        dispatch(setHistorySelectedTime(newHistorySelectedTime));
    }, 500);
    // Приоритетная загрузка с debounce
    const handlePriorityLoad = useDebounce((priorityTime) => {
        if (historyTracksLoading) {
            isPriorityLoadingRef.current = true;
            dispatch(transportHistoryTracksItemsLoadStreamPriority(priorityTime)).then(() => {
                isPriorityLoadingRef.current = false;
            }, () => {
                isPriorityLoadingRef.current = false;
            });
        }
    }, 300);
    // Находим и выбираем ближайшее событие по времени
    const findAndSelectClosestEvent = (targetTime) => {
        if (transportSelectedItem && transportSelectedItem.conditions) {
            const targetMoment = moment(targetTime);
            const conditions = transportSelectedItem.conditions.filter((condition) => condition.type === 'message');
            if (conditions.length > 0) {
                // Находим ближайшее событие по времени
                let closestCondition = conditions[0];
                let minDiff = Math.abs(moment(closestCondition.datetime).diff(targetMoment));
                for (let i = 1; i < conditions.length; i++) {
                    const diff = Math.abs(moment(conditions[i].datetime).diff(targetMoment));
                    if (diff < minDiff) {
                        minDiff = diff;
                        closestCondition = conditions[i];
                    }
                }
                // Выбираем ближайшее событие
                dispatch(setTransportConditionSelectedItem(closestCondition));
            }
        }
    };
    const handleChangeFrom = (newValue) => {
        if (!newValue || !newValue.isValid()) {
            return;
        }
        // Очищаем поле "to" при изменении "from"
        // Нормализуем дату, убирая timezone - используем local() для получения локального времени
        const normalizedDate = moment(newValue).local().format('YYYY-MM-DD HH:mm:ss');
        dispatch(setTransportSelectedDate([normalizedDate, null]));
    };
    // eslint-disable-next-line no-unused-vars
    const handleChangeTo = (_newValue) => {
        // onChange вызывается при каждом изменении, но мы не сохраняем здесь
        // Сохранение происходит только в onAccept
    };
    const handleAcceptTo = (newValue) => {
        if (!newValue || !newValue.isValid()) {
            return;
        }
        // При изменении "to" сразу сохраняем и происходит перезапрос
        // Используем onAccept, чтобы срабатывало только после полного выбора (дата + время)
        // Нормализуем дату, убирая timezone - используем local() для получения локального времени
        const formattedValue = moment(newValue).local().format('YYYY-MM-DD HH:mm:ss');
        dispatch(setTransportSelectedDate([transportSelectedDate[0], formattedValue]));
    };
    const handleChangePrevDay = () => {
        if (!transportSelectedDate[0])
            return;
        const prevDay = moment(transportSelectedDate[0]).startOf('day').subtract(1, 'days');
        const prevDayEnd = prevDay.clone().endOf('day');
        dispatch(setTransportSelectedDate([prevDay.format('YYYY-MM-DD HH:mm:ss'), prevDayEnd.format('YYYY-MM-DD HH:mm:ss')]));
    };
    const handleChangeNextDay = () => {
        if (!transportSelectedDate[0])
            return;
        const nextDay = moment(transportSelectedDate[0]).startOf('day').add(1, 'days');
        const nextDayEnd = nextDay.clone().endOf('day');
        // Проверяем, не превышает ли следующий день максимальную дату
        if (nextDay.isAfter(moment().startOf('day'))) {
            return;
        }
        dispatch(setTransportSelectedDate([nextDay.format('YYYY-MM-DD HH:mm:ss'), nextDayEnd.format('YYYY-MM-DD HH:mm:ss')]));
    };
    const handleChangeOneDayAgo = () => {
        const oneDayAgo = moment().startOf('day').subtract(1, 'days');
        const oneDayAgoEnd = moment().endOf('day').subtract(1, 'days');
        dispatch(setTransportSelectedDate([oneDayAgo.format('YYYY-MM-DD HH:mm:ss'), oneDayAgoEnd.format('YYYY-MM-DD HH:mm:ss')]));
    };
    const handleChangeTwoDaysAgo = () => {
        const twoDaysAgo = moment().startOf('day').subtract(2, 'days');
        const twoDaysAgoEnd = moment().endOf('day').subtract(2, 'days');
        dispatch(setTransportSelectedDate([twoDaysAgo.format('YYYY-MM-DD HH:mm:ss'), twoDaysAgoEnd.format('YYYY-MM-DD HH:mm:ss')]));
    };
    const handleChangeCurrentDay = () => {
        const currentDay = moment().startOf('day');
        const currentDayEnd = moment().endOf('day');
        dispatch(setTransportSelectedDate([currentDay.format('YYYY-MM-DD HH:mm:ss'), currentDayEnd.format('YYYY-MM-DD HH:mm:ss')]));
    };
    const minDateMoment = moment().subtract(3, 'd').startOf('day');
    const maxDateMoment = moment().startOf('day');
    const minDate = minDateMoment.format('YYYY-MM-DD');
    const maxDate = maxDateMoment.format('YYYY-MM-DD');
    const isPrevDayDisabled = useMemo(() => {
        if (!transportSelectedDate[0])
            return true;
        const prevDay = moment(transportSelectedDate[0]).startOf('day').subtract(1, 'days');
        return prevDay.isBefore(minDateMoment);
    }, [transportSelectedDate[0]]);
    const isNextDayDisabled = useMemo(() => {
        if (!transportSelectedDate[0])
            return true;
        const nextDay = moment(transportSelectedDate[0]).startOf('day').add(1, 'days');
        return nextDay.isAfter(maxDateMoment);
    }, [transportSelectedDate[0]]);
    const isOneDayAgoDisabled = useMemo(() => {
        const oneDayAgo = moment().startOf('day').subtract(1, 'days');
        return oneDayAgo.isBefore(minDateMoment);
    }, []);
    const isTwoDaysAgoDisabled = useMemo(() => {
        const twoDaysAgo = moment().startOf('day').subtract(2, 'days');
        return twoDaysAgo.isBefore(minDateMoment);
    }, []);
    const isCurrentDayDisabled = useMemo(() => {
        const currentDay = moment().startOf('day');
        return currentDay.isAfter(maxDateMoment);
    }, []);
    const endDate = transportSelectedDate[1] && moment(transportSelectedDate[1]).isAfter(moment())
        ? moment()
        : transportSelectedDate[1]
            ? moment(transportSelectedDate[1])
            : moment(transportSelectedDate[0]);
    const maxRaw = endDate.diff(startDate, 's');
    const max = isNaN(maxRaw) || maxRaw < 0 ? 0 : maxRaw;
    const safeSelectedTime = isNaN(selectedTime) || selectedTime < 0 ? 0 : Math.min(selectedTime, max);
    const marks = useMemo(() => {
        let marks = [];
        if (transportSelectedDate[0] && transportSelectedDate[1] && max > 0) {
            const msInDay = 24 * 60 * 60;
            marks.push({
                value: 0,
                label: moment(transportSelectedDate[0]).format('DD.MM.YY HH:mm:ss'),
                isDay: true,
            });
            // Основные деления суток
            for (let i = msInDay; i < max; i += msInDay) {
                const markDate = moment(startDate.format()).add(i, 's');
                marks.push({
                    value: i,
                    label: markDate.format('DD.MM.YY'),
                    isDay: true,
                });
            }
            marks.push({
                value: max,
                label: (React.createElement("span", { style: {
                        transform: 'translateX(-50%)',
                        display: 'block',
                    } }, endDate.format('DD.MM.YY HH:mm:ss'))),
                isDay: true,
            });
            // Ежечасные тонкие насечки (теперь каждые 2 часа)
            for (let i = 7200; i < max; i += 7200) {
                // Не добавлять если совпадает с сутками (чтобы не дублировать)
                if (marks.some((m) => m.value === i))
                    continue;
                marks.push({ value: i, isHour: true });
            }
            // Сортировка по value
            marks.sort((a, b) => a.value - b.value);
        }
        return marks;
    }, [transportSelectedDate]);
    // Вычисляем процент загрузки
    const progressPercent = historyTracksProgress.total > 0 ? Math.round((historyTracksProgress.processed / historyTracksProgress.total) * 100) : 0;
    return (React.createElement(Box, { sx: (theme) => ({
            position: 'absolute',
            top: 0,
            right: 0,
            width: 'calc(100vw - 660px)',
            zIndex: 1050,
            backgroundColor: theme.palette.common.white,
        }) },
        historyTracksLoading && (React.createElement(Box, { sx: {
                position: 'absolute',
                top: 50,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1100,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                padding: '12px 20px',
                minWidth: 280,
                backdropFilter: 'blur(8px)',
            } },
            React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 } },
                React.createElement(Box, { sx: {
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        border: '2px solid #1976d2',
                        borderTopColor: 'transparent',
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' },
                        },
                    } }),
                React.createElement(Typography, { variant: 'body2', sx: {
                        color: '#333',
                        fontWeight: 500,
                        fontSize: '13px',
                    } }, "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0438\u0441\u0442\u043E\u0440\u0438\u0438"),
                React.createElement(Typography, { variant: 'body2', sx: {
                        color: '#666',
                        fontSize: '12px',
                        ml: 'auto',
                    } },
                    historyTracksProgress.processed,
                    "/",
                    historyTracksProgress.total)),
            React.createElement(Box, { sx: {
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: '#e0e0e0',
                    overflow: 'hidden',
                } },
                React.createElement(Box, { sx: {
                        height: '100%',
                        width: `${progressPercent}%`,
                        borderRadius: 3,
                        background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 50%, #1976d2 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'progress 1.5s ease-in-out infinite',
                        '@keyframes progress': {
                            '0%': { backgroundPosition: '100% 0' },
                            '100%': { backgroundPosition: '-100% 0' },
                        },
                        transition: 'width 0.3s ease-out',
                    } })),
            React.createElement(Typography, { variant: 'caption', sx: {
                    color: '#888',
                    fontSize: '11px',
                    mt: 0.5,
                    display: 'block',
                    textAlign: 'center',
                } },
                progressPercent,
                "% \u2022 \u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u0434\u0430\u043D\u043D\u044B\u0445 \u0441 \u0441\u0435\u0440\u0432\u0435\u0440\u0430"))),
        React.createElement(Box, { sx: (theme) => ({
                display: 'flex',
                gap: 1,
                flex: 1,
                alignItems: 'center',
                paddingTop: '6px',
                '.MuiInput-root': {
                    display: 'inline-flex',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: '#dddee5',
                    backgroundColor: theme.palette.common.white,
                    padding: '0 4px 0 8px',
                    margin: '5px 10px',
                    width: 'fit-content',
                    minWidth: 'auto',
                    flexShrink: 0,
                    '.MuiInput-input': {
                        width: '19ch',
                        minWidth: '19ch',
                        maxWidth: '19ch',
                        textAlign: 'center',
                        padding: '0',
                    },
                },
            }) },
            React.createElement(LocalizationProvider, { dateAdapter: AdapterMoment, adapterLocale: moment.locale('ru') },
                React.createElement(DateTimePicker, { value: transportSelectedDate[0] ? moment(transportSelectedDate[0]) : null, views: ['day', 'hours', 'minutes'], inputFormat: 'DD.MM.YYYY HH:mm:ss', disableFuture: true, minDate: minDateMoment, maxDate: maxDateMoment, onChange: handleChangeFrom, renderInput: ({ inputRef, inputProps, InputProps }) => {
                        var _a, _b, _c, _d;
                        return (React.createElement(Input, { ref: inputRef, onClick: (_d = (_c = (_b = (_a = InputProps.endAdornment) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.children) === null || _c === void 0 ? void 0 : _c.props) === null || _d === void 0 ? void 0 : _d.onClick, disableUnderline: true, inputProps: Object.assign(Object.assign({}, inputProps), { placeholder: 'Выберите дату' }) }));
                    } }),
                React.createElement(Typography, { sx: {
                        fontSize: '12px',
                    } }, "\u043F\u043E"),
                React.createElement(DateTimePicker, { value: transportSelectedDate[1] ? moment(transportSelectedDate[1]) : null, views: ['day', 'hours', 'minutes'], inputFormat: 'DD.MM.YYYY HH:mm:ss', disableFuture: true, minDate: transportSelectedDate[0] ? moment(transportSelectedDate[0]) : minDateMoment, maxDate: maxDateMoment, onChange: handleChangeTo, onAccept: handleAcceptTo, renderInput: ({ inputRef, inputProps, InputProps }) => {
                        var _a, _b, _c, _d;
                        return (React.createElement(Input, { ref: inputRef, onClick: (_d = (_c = (_b = (_a = InputProps.endAdornment) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.children) === null || _c === void 0 ? void 0 : _c.props) === null || _d === void 0 ? void 0 : _d.onClick, disableUnderline: true, inputProps: Object.assign(Object.assign({}, inputProps), { placeholder: 'Выберите дату' }) }));
                    } })),
            React.createElement(Box, { sx: {
                    display: 'flex',
                    gap: '2px',
                    alignItems: 'center',
                    '.MuiButton-root': {
                        padding: '3px 5px',
                        minWidth: 'auto',
                        background: '#ffffff',
                        border: '1px solid',
                        borderColor: '#ddde',
                        color: '#000000',
                        fontSize: '11px',
                        '&:hover': {
                            background: '#ffffff',
                        },
                        svg: {
                            fontSize: '19px',
                        },
                    },
                } },
                React.createElement(Button, { disabled: isPrevDayDisabled, onClick: handleChangePrevDay },
                    React.createElement(ArrowLeft, { height: '24px', viewBox: '0 -960 960 960', width: '24px' })),
                React.createElement(Button, { disabled: isNextDayDisabled, onClick: handleChangeNextDay },
                    React.createElement(ArrowRight, { height: '24px', viewBox: '0 -960 960 960', width: '24px' })),
                React.createElement(Button, { disabled: isOneDayAgoDisabled, onClick: handleChangeOneDayAgo }, "-1\u0434"),
                React.createElement(Button, { disabled: isTwoDaysAgoDisabled, onClick: handleChangeTwoDaysAgo }, "-2\u0434"),
                React.createElement(Button, { disabled: isCurrentDayDisabled, onClick: handleChangeCurrentDay }, "24\u0447")),
            React.createElement(Box, { sx: {
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                } },
                React.createElement(Typography, { variant: 'caption', sx: {
                        fontSize: '12px',
                    } }, valueLabelFormat)),
            React.createElement(FormControlLabel, { control: React.createElement(Switch, { checked: historyTracksVisible, onChange: (e) => dispatch(setHistoryTracksVisible(e.target.checked)), size: 'small' }), label: '\u041F\u043E\u043A\u0430\u0437\u044B\u0432\u0430\u0442\u044C \u0442\u0440\u0435\u043A \u043D\u0430 \u043A\u0430\u0440\u0442\u0435', labelPlacement: 'start', sx: {
                    marginLeft: 'auto',
                    marginRight: '10px',
                    '& .MuiFormControlLabel-label': {
                        fontSize: '12px',
                    },
                } })),
        React.createElement(Box, { sx: {
                maxWidth: 'calc(100% - 20px)',
                paddingLeft: '10px',
            } },
            React.createElement(Box, { sx: {
                    margin: '10px 0 40px',
                } },
                React.createElement(Slider, { min: 0, max: max, value: safeSelectedTime, valueLabelFormat: valueLabelFormat, onChange: (_, value) => {
                        const nextValue = Array.isArray(value) ? value[0] : value;
                        setSelectedTime(nextValue);
                        const newHistorySelectedTime = moment(startDate.format()).add(nextValue, 's').format('YYYY-MM-DD HH:mm:ss');
                        handleSetHistoryTime(newHistorySelectedTime);
                    }, onChangeCommitted: (_, value) => {
                        const nextValue = Array.isArray(value) ? value[0] : value;
                        const newHistorySelectedTime = moment(startDate.format()).add(nextValue, 's').format('YYYY-MM-DD HH:mm:ss');
                        // Если идёт загрузка, запускаем приоритетную загрузку для выбранного интервала
                        if (historyTracksLoading && !isPriorityLoadingRef.current) {
                            handlePriorityLoad(newHistorySelectedTime);
                        }
                        // Находим и выбираем ближайшее событие после завершения перемещения ползунка
                        findAndSelectClosestEvent(newHistorySelectedTime);
                    }, marks: marks, valueLabelDisplay: 'auto', sx: {
                        width: '100%',
                        py: 0,
                        marginBottom: 0,
                        '.MuiSlider-rail, .MuiSlider-track': {
                            height: 6,
                        },
                        '.MuiSlider-mark': {
                            bgcolor: '#1976d2',
                            height: 8,
                            width: 2,
                            borderRadius: 2,
                            mt: '-2px',
                            // Тонкие ежечасные насечки
                            '&[data-hour="true"]': {
                                bgcolor: '#90caf9',
                                height: 2,
                                width: '1px',
                                borderRadius: 0.5,
                                mt: '1px',
                                opacity: 0.5,
                            },
                        },
                        '.MuiSlider-markLabel': {
                            color: '#1976d2',
                            fontWeight: 600,
                            fontSize: 9,
                            mt: '0px',
                            lineHeight: 1,
                            p: 0,
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&[data-index="0"]': {
                                transform: 'translateX(0)',
                            },
                        },
                    } })))));
};
export default HistoryTimeLime;
